import React, { useState } from 'react';
import {
  Typography,
  Box,
  Button,
  Alert,
  Switch,
  FormControlLabel,
  Divider,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../../firebase/config';

const PaymentSection = ({ userProfile, expandedSection, onAccordionChange, setSuccess, setError, currentUser, isMobile }) => {
  const [paymentSettings, setPaymentSettings] = useState({
    prepaymentEnabled: userProfile.paymentSettings?.prepaymentEnabled || false,
    onlinePaymentEnabled: userProfile.paymentSettings?.onlinePaymentEnabled || false
  });
  const [stripeLoading, setStripeLoading] = useState(false);

  const handlePaymentSettingChange = async (setting, value) => {
    try {
      const newSettings = { ...paymentSettings, [setting]: value };
      setPaymentSettings(newSettings);

      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        [`paymentSettings.${setting}`]: value,
        updatedAt: new Date()
      });

      setSuccess('Paramètre de paiement mis à jour');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error updating payment setting:', error);
      setError('Erreur lors de la mise à jour des paramètres de paiement');
      setPaymentSettings(prev => ({ ...prev, [setting]: !value }));
    }
  };

  const activateStripeAccount = async () => {
    setStripeLoading(true);
    try {
      const createStripeAccountLink = httpsCallable(functions, 'createStripeAccountLink');
      const result = await createStripeAccountLink({
        salonName: userProfile.salonName,
        email: currentUser.email
      });

      const { url } = result.data;
      window.location.href = url;
    } catch (error) {
      console.error('Error activating Stripe:', error);
      setError('Erreur lors de l\'activation de Stripe. Veuillez réessayer.');
    } finally {
      setStripeLoading(false);
    }
  };

  const manageStripeAccount = async () => {
    setStripeLoading(true);
    try {
      const createStripeLoginLink = httpsCallable(functions, 'createStripeLoginLink');
      const result = await createStripeLoginLink();

      const { url } = result.data;
      window.open(url, '_blank');
    } catch (error) {
      console.error('Error accessing Stripe dashboard:', error);
      setError('Erreur lors de l\'accès au tableau de bord Stripe');
    } finally {
      setStripeLoading(false);
    }
  };

  const colors = {
    primary: '#000000',
    secondary: '#6B7280',
    accent: '#F5F5DC',
    background: '#F8F9FA',
    surface: '#FFFFFF',
    border: '#E5E7EB'
  };

  const content = (
    <Box sx={{ p: { xs: 0.5, sm: 1, md: 4 } }}>
      {!isMobile && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: 600,
            color: colors.primary,
            mb: 3,
            fontSize: { xs: '1.125rem', md: '1.25rem' }
          }}
        >
          Paramètres de paiement
        </Typography>
      )}

      <Typography 
        variant="body2" 
        sx={{ 
          color: colors.secondary, 
          mb: 3,
          lineHeight: 1.6,
          fontSize: { xs: '0.875rem', md: '1rem' }
        }}
      >
        Configurez les options de paiement pour vos clients
      </Typography>

      <Stack spacing={3}>
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={paymentSettings.prepaymentEnabled}
                onChange={(e) => handlePaymentSettingChange('prepaymentEnabled', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Activer le prépaiement
              </Typography>
            }
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary, 
              ml: 4, 
              mt: 0.5,
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}
          >
            Permettre aux clients de payer à l'avance lors de la réservation
          </Typography>
        </Box>

        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={paymentSettings.onlinePaymentEnabled}
                onChange={(e) => handlePaymentSettingChange('onlinePaymentEnabled', e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': {
                    color: colors.primary,
                  },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                    backgroundColor: colors.primary,
                  },
                }}
              />
            }
            label={
              <Typography sx={{ 
                fontSize: { xs: '0.875rem', md: '1rem' },
                fontWeight: 500,
                color: colors.primary
              }}>
                Activer le paiement en ligne
              </Typography>
            }
          />
          <Typography 
            variant="body2" 
            sx={{ 
              color: colors.secondary, 
              ml: 4, 
              mt: 0.5,
              fontSize: { xs: '0.75rem', md: '0.875rem' }
            }}
          >
            Accepter les paiements par carte bancaire via Stripe
          </Typography>
        </Box>

        {(paymentSettings.prepaymentEnabled || paymentSettings.onlinePaymentEnabled) && (
          <Box sx={{ mt: 3 }}>
            <Divider sx={{ my: 3, borderColor: colors.border }} />
            <Typography 
              variant="subtitle1" 
              sx={{ 
                fontWeight: 600,
                color: colors.primary,
                mb: 2,
                fontSize: { xs: '1rem', md: '1.125rem' }
              }}
            >
              Compte Stripe
            </Typography>
            
            {userProfile.stripeAccountId ? (
              <Box>
                <Alert 
                  severity="success" 
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }
                  }}
                >
                  Votre compte Stripe est configuré et actif
                </Alert>
                <Button
                  variant="outlined"
                  onClick={manageStripeAccount}
                  disabled={stripeLoading}
                  fullWidth
                  sx={{
                    borderColor: colors.primary,
                    color: colors.primary,
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    '&:hover': {
                      borderColor: colors.primary,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    }
                  }}
                >
                  {stripeLoading ? 'Chargement...' : 'Gérer mon compte Stripe'}
                </Button>
              </Box>
            ) : (
              <Box>
                <Alert 
                  severity="warning" 
                  sx={{ 
                    mb: 2,
                    borderRadius: 2,
                    '& .MuiAlert-message': {
                      fontSize: { xs: '0.875rem', md: '1rem' }
                    }
                  }}
                >
                  Vous devez configurer votre compte Stripe pour accepter les paiements
                </Alert>
                <Button
                  variant="contained"
                  onClick={activateStripeAccount}
                  disabled={stripeLoading}
                  fullWidth
                  sx={{
                    backgroundColor: '#635bff',
                    color: 'white',
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    fontWeight: 600,
                    borderRadius: 2,
                    textTransform: 'none',
                    boxShadow: '0 2px 8px rgba(99, 91, 255, 0.2)',
                    '&:hover': {
                      backgroundColor: '#5a52e8',
                      boxShadow: '0 4px 12px rgba(99, 91, 255, 0.3)'
                    }
                  }}
                >
                  {stripeLoading ? 'Configuration...' : 'Activer les paiements Stripe'}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Stack>
    </Box>
  );

  if (isMobile) {
    return (
      <Accordion 
        expanded={expandedSection === 'payment'} 
        onChange={onAccordionChange('payment')}
        elevation={0}
        sx={{ 
          '&:before': { display: 'none' },
          borderRadius: '0 !important'
        }}
      >
        <AccordionSummary 
          expandIcon={<ExpandMoreIcon />}
          sx={{ 
            px: 3,
            py: 2,
            minHeight: 'auto',
            '&.Mui-expanded': {
              minHeight: 'auto'
            },
            '& .MuiAccordionSummary-content': {
              margin: 0,
              '&.Mui-expanded': {
                margin: 0
              }
            }
          }}
        >
          <Typography 
            variant="h6" 
            sx={{ 
              fontWeight: 600,
              color: colors.primary,
              fontSize: '1.125rem'
            }}
          >
            Paramètres de paiement
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ p: 0 }}>
          {content}
        </AccordionDetails>
      </Accordion>
    );
  }

  return content;
};

export default PaymentSection;